
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  subnav: true,
  maxTocLevel: 4,
  layout: 'reference',
  secondLevelToc: true,
  alwaysExpand: true,
  parentTocLevel: 3,
  childTocLevel: 4,
  description: 'API reference for endpoints to manage your Plaid Ledger',
  source: 'endpoint'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Plaid Ledger' subtitle='API reference for Plaid Ledger' compact mdxType="Header" />
    <p>{`For how-to guidance, see the `}<a parentName="p" {...{
        "href": "/docs/transfer/flow-of-funds"
      }}>{`Ledger documentation`}</a>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Plaid Ledger`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/transfer/ledger/deposit`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Deposit funds into a ledger balance held with Plaid`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/transfer/ledger/distribute`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Move available balance between platform and its originator`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/transfer/ledger/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Retrieve information about the ledger balance held with Plaid`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/transfer/ledger/withdraw`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Withdraw funds from a ledger balance held with Plaid`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/transfer/ledger/event/list`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Retrieve a list of ledger balance events`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "transferledgerdeposit"
    }}>{`/transfer/ledger/deposit`}</h4>
    <Schema method='POST' route='/transfer/ledger/deposit' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/transfer/ledger/deposit' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: TransferLedgerDepositRequest = {
  amount: '12.34',
  network: 'ach',
  idempotency_key: 'test_deposit_abc',
  description: 'deposit',
};
try {
  const response = await client.transferLedgerDeposit(request);
  const sweep = response.data.sweep;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/transfer/ledger/deposit \\
 -H 'Content-Type: application/json' \\
 -d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "amount": "12.34",
   "network": "ach",
   "idempotency_key": "test_deposit_abc",
   "description": "deposit"
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::TransferLedgerDepositRequest.new(
  {
   amount: "12.34",
   network: "ach",
   idempotency_key: "test_deposit_abc",
   description: "deposit",
  }
)
response = client.transfer_ledger_deposit(request)
sweep = response.sweep
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`TransferLedgerDepositRequest request = new TransferLedgerDepositRequest()
  .amount("12.34")
  .network(TransferACHNetwork.ACH)
  .idempotencyKey("test_deposit_abc")
  .description("deposit");
Response<TransferLedgerDepositResponse> response = client()
  .transferLedgerDeposit(request)
  .execute();
TransferSweep sweep =
  response.body().getSweep();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = TransferLedgerDepositRequest(
    amount='12.34',
    network=TransferACHNetwork('ach'),
    idempotency_key=LedgerDepositIdempotencyKey('test_deposit_abc'),
    description=SweepDescription('deposit'),
)
response = client.transfer_ledger_deposit(request)
sweep = response['sweep']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewTransferLedgerDepositRequest(
  "12.34",
  "test_deposit_abc",
  "ach",
)
response, _, err := client.PlaidApi.TransferLedgerDeposit(ctx).TransferLedgerDepositRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`import (
    transfer "github.com/plaid/plaid-go/v49/transfer"
)

request := &transfer.TransferLedgerDepositRequest{
    Amount:         "12.34",
    IdempotencyKey: "test_deposit_abc",
    Network:        "ach",
}
response, err := client.Transfer.Ledger.Deposit(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/transfer/ledger/deposit' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "transferledgerdistribute"
    }}>{`/transfer/ledger/distribute`}</h4>
    <Schema method='POST' route='/transfer/ledger/distribute' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/transfer/ledger/distribute' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: TransferLedgerDistributeRequest = {
   from_ledger_id: 'ec07bbf3-a3d4-4ada-8be0-7be9a6f9bcd0',
   to_ledger_id: '0b13ddd5-fa46-432f-8e5d-7e8ea6f8c8b1',
   amount: '12.34',
   idempotency_key: 'test_distribute_abc',
   description: 'distribute',
};
try {
  const response = await client.transferLedgerDistribute(request);
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/transfer/ledger/distribute \\
 -H 'Content-Type: application/json' \\
 -d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "amount": "12.34",
   "from_ledger_id": "ec07bbf3-a3d4-4ada-8be0-7be9a6f9bcd0",
   "to_ledger_id": "0b13ddd5-fa46-432f-8e5d-7e8ea6f8c8b1",
   "idempotency_key": "test_distribute_abc",
   "description": "distribute"
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::TransferLedgerDistributeRequest.new(
  {
   amount: "12.34",
   from_ledger_id: "ec07bbf3-a3d4-4ada-8be0-7be9a6f9bcd0",
   to_ledger_id: "0b13ddd5-fa46-432f-8e5d-7e8ea6f8c8b1",
   idempotency_key: "test_distribute_abc",
   description: "distribute",
  }
)
response = client.transfer_ledger_distribute(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`TransferLedgerDistributeRequest request = new TransferLedgerDistributeRequest()
  .fromLedgerId("ec07bbf3-a3d4-4ada-8be0-7be9a6f9bcd0")
  .toLedgerId("0b13ddd5-fa46-432f-8e5d-7e8ea6f8c8b1")
  .amount("12.34")
  .idempotencyKey("test_distribute_abc")
  .description("distribute");
Response<TransferLedgerDistributeResponse> response = client()
  .transferLedgerDistribute(request)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = TransferLedgerDistributeRequest(
    amount='12.34',
    from_ledger_id="ec07bbf3-a3d4-4ada-8be0-7be9a6f9bcd0",
    to_ledger_id="0b13ddd5-fa46-432f-8e5d-7e8ea6f8c8b1",
    idempotency_key=LedgerDistributeIdempotencyKey('test_distribute_abc'),
    description='distribute',
)
response = client.transfer_ledger_distribute(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewTransferLedgerDistributeRequest(
  "ec07bbf3-a3d4-4ada-8be0-7be9a6f9bcd0",
  "0b13ddd5-fa46-432f-8e5d-7e8ea6f8c8b1",
  "12.34",
  "test_distribute_abc",
)
request.SetDescription("distribute")
response, _, err := client.PlaidApi.TransferLedgerDistribute(ctx).TransferLedgerDistributeRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`import (
    transfer "github.com/plaid/plaid-go/v49/transfer"
)

request := &transfer.TransferLedgerDistributeRequest{
    FromLedgerID:   "ec07bbf3-a3d4-4ada-8be0-7be9a6f9bcd0",
    ToLedgerID:     "0b13ddd5-fa46-432f-8e5d-7e8ea6f8c8b1",
    Amount:         "12.34",
    IdempotencyKey: "test_distribute_abc",
}
request.Description = plaid.String("distribute")
response, err := client.Transfer.Ledger.Distribute(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/transfer/ledger/distribute' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "transferledgerget"
    }}>{`/transfer/ledger/get`}</h4>
    <Schema method='POST' route='/transfer/ledger/get' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/transfer/ledger/get' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`try {
  const response = await client.transferLedgerGet({});
  const available_balance = response.data.balance.available;
  const pending_balance = response.data.balance.pending;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/transfer/ledger/get \\
 -H 'Content-Type: application/json' \\
 -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`response = client.transfer_ledger_get({})
available_balance = response.balance.available
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`Response<TransferLedgerGetResponse> response = client().transferLedgerGet(new TransferLedgerGetRequest()).execute();

String availableBalance =
  response.body().getBalance().getAvailable();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`response = client.transfer_ledger_get({})
available_balance = response['balance']['available']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`  resp, _, err := client.PlaidApi.TransferLedgerGet(ctx).TransferLedgerGetRequest(*plaid.NewTransferLedgerGetRequest()).Execute()
  if err != nil {
    plaidErr, _ := plaid.ToPlaidError(err)
    fmt.Println(plaidErr.ErrorMessage)
  }
  ledgerBalance := resp.GetBalance()
  balance := ledgerBalance.GetAvailable()

`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`import (
    "errors"
    transfer "github.com/plaid/plaid-go/v49/transfer"
)

resp, err := client.Transfer.Ledger.Get(ctx, &transfer.TransferLedgerGetRequest{})
if err != nil {
    var plaidErr interface{ GetBody() *plaid.PlaidError }
    if errors.As(err, &plaidErr) && plaidErr.GetBody() != nil {
        fmt.Println(plaidErr.GetBody().ErrorMessage)
    } else {
        fmt.Println(err)
    }
}
ledgerBalance := resp.GetBalance()
balance := ledgerBalance.GetAvailable()
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/transfer/ledger/get' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "transferledgerwithdraw"
    }}>{`/transfer/ledger/withdraw`}</h4>
    <Schema method='POST' route='/transfer/ledger/withdraw' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/transfer/ledger/withdraw' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: TransferLedgerWithdrawRequest = {
  amount: '12.34',
  network: 'ach',
  idempotency_key: 'test_withdraw_abc',
  description: 'withdraw',
};
try {
  const response = await client.transferLedgerWithdraw(request);
  const sweep = response.data.sweep;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/transfer/ledger/withdraw \\
 -H 'Content-Type: application/json' \\
 -d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "amount": "12.34",
   "network": "ach",
   "idempotency_key": "test_withdraw_abc",
   "description": "withdraw"
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::TransferLedgerWithdrawRequest.new(
  {
   amount: "12.34",
   network: "ach",
   idempotency_key: "test_withdraw_abc",
   description: "withdraw",
  }
)
response = client.transfer_ledger_withdraw(request)
sweep = response.sweep
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`TransferLedgerWithdrawRequest request = new TransferLedgerWithdrawRequest()
  .amount("12.34")
  .network(TransferNetwork.ACH)
  .idempotencyKey("test_withdraw_abc")
  .description("withdraw");
Response<TransferLedgerWithdrawResponse> response = client()
  .transferLedgerWithdraw(request)
  .execute();
TransferSweep sweep =
  response.body().getSweep();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = TransferLedgerWithdrawRequest(
    amount='12.34',
    network=TransferNetwork('ach'),
    idempotency_key=LedgerWithdrawIdempotencyKey('test_withdraw_abc'),
    description=SweepDescription('withdraw'),
)
response = client.transfer_ledger_withdraw(request)
sweep = response['sweep']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewTransferLedgerWithdrawRequest(
  "12.34",
  "test_withdraw_abc",
  "ach",
)
response, _, err := client.PlaidApi.TransferLedgerWithdraw(ctx).TransferLedgerWithdrawRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`import (
    transfer "github.com/plaid/plaid-go/v49/transfer"
)

request := &transfer.TransferLedgerWithdrawRequest{
    Amount:         "12.34",
    IdempotencyKey: "test_withdraw_abc",
    Network:        "ach",
}
response, err := client.Transfer.Ledger.Withdraw(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/transfer/ledger/withdraw' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "transferledgereventlist"
    }}>{`/transfer/ledger/event/list`}</h4>
    <Schema method='POST' route='/transfer/ledger/event/list' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/transfer/ledger/event/list' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request: TransferLedgerEventListRequest = {
    originator_client_id: "61b8f48ded273e001aa8db6d",
    start_date: '2019-12-06T22:35:49Z',
    end_date: '2019-12-12T22:35:49Z',
    count: 14,
    offset: 2,
    ledger_id: "563db5f8-4c95-4e17-8c3e-cb988fb9cf1a",
    ledger_event_id: "3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
    source_type: "TRANSFER",
    source_id: "460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9",
 };
try {
  const response = await plaidClient.transferLedgerEventList(request);
  const events = response.data.ledger_events;
  for (const event of events) {
    // iterate through events
  }
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/transfer/ledger/event/list \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "originator_client_id": "61b8f48ded273e001aa8db6d",
    "start_date": "2019-12-06T22:35:49Z",
    "end_date": "2019-12-12T22:35:49Z",
    "count": 14,
    "offset": 0,
    "ledger_id": "563db5f8-4c95-4e17-8c3e-cb988fb9cf1a",
    "ledger_event_id": "3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
    "source_type": "TRANSFER",
    "source_id": "460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9"
  }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`require 'time'

request = Plaid::TransferLedgerEventListRequest.new(
  {
    originator_client_id: '61b8f48ded273e001aa8db6d',
    start_date: Time.parse('2019-12-06T22:35:49Z'),
    end_date: Time.parse('2019-12-12T22:35:49Z'),
    count: 14,
    offset: 2,
    ledger_id: '563db5f8-4c95-4e17-8c3e-cb988fb9cf1a',
    ledger_event_id: '3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr',
    source_type: 'TRANSFER',
    source_id: '460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9',
  }
)
response = client.transfer_ledger_event_list(request)
response.ledger_events.each do |event|
  # iterate through events
end
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`import java.time.OffsetDateTime;
import java.util.ArrayList;
import java.util.List;

OffsetDateTime startDate = OffsetDateTime.parse("2019-12-06T22:35:49+00:00");
OffsetDateTime endDate = OffsetDateTime.parse("2019-12-12T22:35:49+00:00");

TransferLedgerEventListRequest request = new TransferLedgerEventListRequest()
  .originatorClientId("61b8f48ded273e001aa8db6d")
  .startDate(startDate)
  .endDate(endDate)
  .count(14)
  .offset(2)
  .ledgerId("563db5f8-4c95-4e17-8c3e-cb988fb9cf1a")
  .ledgerEventId("3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr")
  .sourceType(LedgerEventSourceType.TRANSFER)
  .sourceId("460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9");
Response<TransferLedgerEventListResponse> response = client()
  .transferLedgerEventList(request)
  .execute();
List<TransferLedgerEvent> ledgerEvents =
  response.body().getLedgerEvents();
for (TransferLedgerEvent e : ledgerEvents) {
  // iterate through events
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`import datetime

request = TransferLedgerEventListRequest(
  originator_client_id='61b8f48ded273e001aa8db6d',
  start_date=datetime.datetime(2019, 12, 6, 22, 35, 49, tzinfo=datetime.timezone.utc),
  end_date=datetime.datetime(2019, 12, 12, 22, 35, 49, tzinfo=datetime.timezone.utc),
  count=14,
  offset=2,
  ledger_id='563db5f8-4c95-4e17-8c3e-cb988fb9cf1a',
  ledger_event_id='3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr',
  source_type=LedgerEventSourceType('TRANSFER'),
  source_id='460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9'
)
response = client.transfer_ledger_event_list(request)
events = response['ledger_events']
for event in events:
  # iterate through events
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewTransferLedgerEventListRequest()

request.SetOriginatorClientId("61b8f48ded273e001aa8db6d")
request.SetStartDate(time.Date(2019, time.December, 6, 22, 35, 49, 0, time.UTC))
request.SetEndDate(time.Date(2019, time.December, 12, 22, 35, 49, 0, time.UTC))
request.SetCount(14)
request.SetOffset(2)
request.SetLedgerId("563db5f8-4c95-4e17-8c3e-cb988fb9cf1a")
request.SetLedgerEventId("3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr")
request.SetSourceType("TRANSFER")
request.SetSourceId("460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9")

response, _, err := client.PlaidApi.TransferLedgerEventList(ctx).TransferLedgerEventListRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`import (
    transfer "github.com/plaid/plaid-go/v49/transfer"
)

request := &transfer.TransferLedgerEventListRequest{}

request.OriginatorClientID = plaid.String("61b8f48ded273e001aa8db6d")
request.StartDate = plaid.Time(time.Date(2019, time.December, 6, 22, 35, 49, 0, time.UTC))
request.EndDate = plaid.Time(time.Date(2019, time.December, 12, 22, 35, 49, 0, time.UTC))
request.Count = plaid.Int(14)
request.Offset = plaid.Int(2)
request.LedgerID = plaid.String("563db5f8-4c95-4e17-8c3e-cb988fb9cf1a")
request.LedgerEventID = plaid.String("3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr")
request.SourceType = plaid.LedgerEventSourceType("TRANSFER").Ptr()
request.SourceID = plaid.String("460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9")

response, err := client.Transfer.Ledger.ListEvents(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/transfer/ledger/event/list' type='response' mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "transferledgerdeposit",
        level: 4,
        title: "/transfer/ledger/deposit",
      },
{
        id: "transferledgerdistribute",
        level: 4,
        title: "/transfer/ledger/distribute",
      },
{
        id: "transferledgerget",
        level: 4,
        title: "/transfer/ledger/get",
      },
{
        id: "transferledgerwithdraw",
        level: 4,
        title: "/transfer/ledger/withdraw",
      },
{
        id: "transferledgereventlist",
        level: 4,
        title: "/transfer/ledger/event/list",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
